import { useState, useEffect, useRef } from "react";
import { SmartChipButton } from "./SmartChipButton";

interface TextSelectionSmartChipProps {
  tableSchema?: {
    fields: Array<{ name: string; type: string; config?: any }>;
  };
  onEntitiesExtracted: (entities: Array<Record<string, any>>) => void;
}

export function TextSelectionSmartChip({ tableSchema, onEntitiesExtracted }: TextSelectionSmartChipProps) {
  const [selectedText, setSelectedText] = useState("");
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleSelection = () => {
      const selection = window.getSelection();
      const text = selection?.toString().trim() || "";

      if (text && text.length > 0) {
        setSelectedText(text);

        // Get selection position
        const range = selection?.getRangeAt(0);
        const rect = range?.getBoundingClientRect();

        if (rect) {
          setPosition({
            top: rect.bottom + window.scrollY + 8,
            left: rect.left + window.scrollX + (rect.width / 2)
          });
        }
      } else {
        setSelectedText("");
        setPosition(null);
      }
    };

    document.addEventListener("mouseup", handleSelection);
    document.addEventListener("keyup", handleSelection);

    return () => {
      document.removeEventListener("mouseup", handleSelection);
      document.removeEventListener("keyup", handleSelection);
    };
  }, []);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setSelectedText("");
        setPosition(null);
      }
    };

    if (position) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [position]);

  if (!selectedText || !position) return null;

  return (
    <div
      ref={buttonRef}
      style={{
        position: "absolute",
        top: `${position.top}px`,
        left: `${position.left}px`,
        transform: "translateX(-50%)",
        zIndex: 1000,
      }}
      className="animate-in fade-in slide-in-from-bottom-2 duration-200"
    >
      <SmartChipButton
        selectedText={selectedText}
        tableSchema={tableSchema}
        onEntitiesExtracted={(entities) => {
          onEntitiesExtracted(entities);
          setSelectedText("");
          setPosition(null);
        }}
      />
    </div>
  );
}
